import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { AlertTriangle } from "lucide-react";
import { api } from "../../../lib/api";
import { money, StatusPill } from "./common";

export default function Stats() {
    const [stats, setStats] = useState(null);

    useEffect(() => {
        api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    }, []);

    if (!stats) return <p className="text-slate-500" data-testid="stats-loading">Loading dashboard…</p>;

    const cards = [
        { label: "Total Sales", value: money(stats.total_sales), testId: "stat-total-sales" },
        { label: "Paid Orders", value: stats.paid_orders, testId: "stat-paid-orders" },
        { label: "Orders Today", value: stats.orders_today, testId: "stat-orders-today" },
        { label: "Sales Today", value: money(stats.sales_today), testId: "stat-sales-today" },
        { label: "Products", value: stats.product_count, testId: "stat-products" },
        { label: "Newsletter Subscribers", value: stats.newsletter_subscribers, testId: "stat-subscribers" },
    ];

    return (
        <div data-testid="admin-dashboard" className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
                {cards.map((c) => (
                    <div key={c.label} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={c.testId}>
                        <p className="text-xs text-slate-500">{c.label}</p>
                        <p className="font-heading text-2xl font-extrabold text-[#0033A0] mt-1">{c.value}</p>
                    </div>
                ))}
            </div>

            {stats.low_stock.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5" data-testid="low-stock-alerts">
                    <p className="font-heading font-bold text-amber-900 flex items-center gap-2 mb-3">
                        <AlertTriangle className="h-5 w-5" /> Low stock alerts ({stats.low_stock.length})
                    </p>
                    <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {stats.low_stock.map((p) => (
                            <li key={`${p.id}-${p.name}`} className="bg-white rounded-xl border border-amber-200 px-4 py-2.5 text-sm flex justify-between" data-testid={`low-stock-${p.id}`}>
                                <span className="font-bold text-slate-700 truncate">{p.name}</span>
                                <span className={`font-bold ${p.stock === 0 ? "text-red-600" : "text-amber-700"}`}>
                                    {p.stock === 0 ? "Out of stock" : `${p.stock} left`}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {stats.failed_emails > 0 && (
                <p className="bg-red-50 border border-red-200 text-red-700 rounded-2xl px-5 py-4 text-sm font-bold" data-testid="failed-email-alert">
                    {stats.failed_emails} email{stats.failed_emails > 1 ? "s" : ""} failed to send — check the Emails section to retry.
                </p>
            )}

            <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="sales-chart-card">
                    <p className="font-heading font-bold text-slate-900 mb-4">Sales — last 30 days</p>
                    <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={stats.sales_series}>
                                <XAxis dataKey="date" tickFormatter={(d) => d.slice(8)} fontSize={11} />
                                <YAxis fontSize={11} tickFormatter={(v) => `£${v}`} width={55} />
                                <Tooltip formatter={(v) => [money(v), "Sales"]} labelFormatter={(d) => d} />
                                <Area type="monotone" dataKey="total" stroke="#0033A0" fill="#40E0D0" fillOpacity={0.35} strokeWidth={2} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="recent-orders-card">
                    <p className="font-heading font-bold text-slate-900 mb-4">Recent orders</p>
                    {stats.recent_orders.length === 0 ? (
                        <p className="text-sm text-slate-400">No orders yet.</p>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {stats.recent_orders.map((o) => (
                                <li key={o.id} className="py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`recent-order-${o.order_number}`}>
                                    <div>
                                        <span className="font-bold text-[#0033A0]">{o.order_number}</span>
                                        <span className="text-slate-500 ml-2">{o.customer}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-bold">{money(o.total)}</span>
                                        <StatusPill value={o.status} />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="status-overview-card">
                <p className="font-heading font-bold text-slate-900 mb-3">Orders by status</p>
                <div className="flex flex-wrap gap-2">
                    {Object.entries(stats.status_counts).map(([s, n]) => (
                        <span key={s} className="rounded-full bg-slate-100 px-4 py-1.5 text-sm font-bold text-slate-700" data-testid={`status-count-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                            {s}: {n}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
}
